import { Game, UserGame } from '../../../types/index';

/**
 * Utilidades para processamento de jogos
 * Contém métodos para manipulação e filtragem de conjuntos de jogos
 */
export class GameProcessingUtils {
  /**
   * Extrai categorias, mecânicas e temas favoritos do usuário a partir
   * de uma lista de jogos (Game[])
   */
  static extractFavoriteAttributes(gamesList: Game[]): {
    categories: Set<string>;
    mechanics: Set<string>;
    themes: Set<string>;
    gameIds: Set<string>;
    preferredComplexity?: number;
  } {
    const favoriteCategories = new Set<string>();
    const favoriteMechanics = new Set<string>();
    const favoriteThemes = new Set<string>();
    const favoriteGameIds = new Set<string>();
    
    let complexitySum = 0;
    let complexityCount = 0;

    // Processamento direto da lista de jogos
    gamesList.forEach(game => {
      if (!game || typeof game !== 'object' || !game.id) return;
      
      favoriteGameIds.add(game.id);
      
      // Processar categorias diretamente do objeto game
      const gameCategories = game.categories;
      if (Array.isArray(gameCategories)) {
        gameCategories.forEach(cat => {
          if (typeof cat === 'string') favoriteCategories.add(cat);
        });
      }
      
      // Processar mecânicas diretamente do objeto game
      const gameMechanics = game.mechanics;
      if (Array.isArray(gameMechanics)) {
        gameMechanics.forEach(mech => {
          if (typeof mech === 'string') favoriteMechanics.add(mech);
        });
      }
      
      // Processar temas diretamente do objeto game
      const gameThemes = game.themes;
      if (Array.isArray(gameThemes)) {
        gameThemes.forEach(theme => {
          if (typeof theme === 'string') favoriteThemes.add(theme);
        });
      }
      
      // Coletar informações sobre complexidade do jogo
      if (typeof game.complexity === 'number') {
        complexitySum += game.complexity;
        complexityCount++;
      }
    });
    
    // Calcular complexidade preferida baseada apenas nos jogos
    let preferredComplexity: number | undefined;
    if (complexityCount > 0) {
      preferredComplexity = complexitySum / complexityCount;
    }

    return {
      categories: favoriteCategories,
      mechanics: favoriteMechanics,
      themes: favoriteThemes,
      gameIds: favoriteGameIds,
      preferredComplexity
    };
  }

  /**
   * Calcula a pontuação de similaridade entre um jogo e as preferências do usuário
   */
  static calculateSimilarityScore(
    game: Game,
    favoriteCategories: Set<string>,
    favoriteMechanics: Set<string>,
    favoriteThemes: Set<string>
  ): number {
    let score = 0;
    
    // Categorias em comum
    const commonCategories = Array.isArray(game.categories) ? 
      game.categories.filter(cat => favoriteCategories.has(cat)) : [];
    score += commonCategories.length * 10;
    
    // Mecânicas em comum
    const commonMechanics = game.mechanics && Array.isArray(game.mechanics) ?
      game.mechanics.filter(mech => favoriteMechanics.has(mech)) : [];
    score += commonMechanics.length * 8;
    
    // Temas em comum
    const commonThemes = game.themes && Array.isArray(game.themes) ?
      game.themes.filter(theme => favoriteThemes.has(theme)) : [];
    score += commonThemes.length * 5;
    
    // Adicionar pontos para jogos mais recentes
    const createdDate = new Date(game.created_at);
    const now = new Date();
    const ageInDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    // Jogos mais recentes recebem pontuação extra (máximo 20 pontos)
    const freshness = Math.max(0, 20 - ageInDays / 30); // 20 pontos para jogos novos, diminui com o tempo
    score += freshness;
    
    return score;
  }

  /**
   * Gera explicações para as recomendações baseadas na similaridade
   */
  static generateRecommendationReason(
    game: Game,
    favoriteCategories: Set<string>,
    favoriteMechanics: Set<string>
  ): string {
    // Identificar categorias em comum
    const commonCategories = Array.isArray(game.categories) ? 
      game.categories.filter(cat => favoriteCategories.has(cat)) : [];
    
    // Identificar mecânicas em comum
    const commonMechanics = game.mechanics && Array.isArray(game.mechanics) ?
      game.mechanics.filter(mech => favoriteMechanics.has(mech)) : [];
    
    if (commonCategories.length > 0 && commonMechanics.length > 0) {
      return `Recomendado porque você gosta de jogos com ${commonCategories.slice(0, 2).join(', ')} e mecânicas como ${commonMechanics.slice(0, 2).join(', ')}`;
    } else if (commonCategories.length > 0) {
      return `Recomendado porque você gosta de jogos com ${commonCategories.slice(0, 3).join(', ')}`;
    } else if (commonMechanics.length > 0) {
      return `Recomendado por usar mecânicas que você gosta: ${commonMechanics.slice(0, 3).join(', ')}`;
    } else if (game.average_rating && game.average_rating > 7.5) {
      return `Um jogo muito bem avaliado pela comunidade (${game.average_rating.toFixed(1)}/10)`;
    } else {
      return `Um jogo que pode combinar com seu perfil de jogador`;
    }
  }

  /**
   * Embaralha um array de jogos usando o algoritmo de Fisher-Yates
   */
  static shuffleArray<T>(array: T[]): T[] {
    if (!array || !Array.isArray(array)) return [];
    
    // Cria uma cópia para não modificar o array original
    const shuffled = [...array];
    
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    return shuffled;
  }

  /**
   * Remove jogos duplicados de uma lista
   */
  static removeDuplicateGames(games: Game[]): Game[] {
    if (!games || !Array.isArray(games)) return [];
    
    // Usar Map para remover duplicatas baseadas no ID
    const uniqueGames = new Map<string, Game>();
    
    games.forEach(game => {
      if (game && game.id) {
        uniqueGames.set(game.id, game);
      }
    });
    
    return Array.from(uniqueGames.values());
  }
  
  /**
   * Filtra jogos já vistos/excluídos de uma lista de jogos
   */
  static filterExcludedGames(games: Game[], excludeIds: string[]): Game[] {
    if (!games || !Array.isArray(games)) return [];
    if (!excludeIds || !Array.isArray(excludeIds) || excludeIds.length === 0) return games;
    
    // Criar Set para busca eficiente de IDs
    const excludeSet = new Set(excludeIds);
    
    // Filtrar jogos não presentes na lista de exclusão
    return games.filter(game => game && game.id && !excludeSet.has(game.id));
  }

  /**
   * Ordena jogos por similaridade com as preferências do usuário
   */
  static sortGamesBySimilarity(
    games: Game[],
    categories: Set<string>,
    mechanics: Set<string>,
    themes: Set<string>
  ): Game[] {
    if (!games || !Array.isArray(games) || games.length === 0) return [];
    
    // Calcular pontuação para cada jogo
    const scoredGames = games.map(game => {
      const score = this.calculateSimilarityScore(game, categories, mechanics, themes);
      return { game, score };
    });
    
    // Ordenar por pontuação (maior para menor)
    scoredGames.sort((a, b) => b.score - a.score);
    
    // Retornar apenas os jogos, sem as pontuações
    return scoredGames.map(item => item.game);
  }
} 